import { Component, Inject, inject, OnDestroy } from "@angular/core";
import { filter, take, Subscription } from 'rxjs';
import { AmplifyUser } from '@aws-amplify/ui';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { ConfigService } from "../../config.service";
import { TUI_IS_MOBILE } from "@taiga-ui/cdk";
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { TUI_COUNTRIES_MASKS } from '@taiga-ui/kit';
import { AuthService } from "src/app/services/auth.service";
import { MatDialog } from '@angular/material/dialog';
import { AWSLambda } from 'src/app/AWSLambda.service';

import { CountriesModalComponent } from '../rates/countries-modal.component';
import { RatesModalComponent } from '../rates/rates-modal.component';
import { Rate, RatesModal } from '../rates/rates.model';
import { getCountryInfo } from '../recharges/recharges.countries';
import { BalanceService } from "../../services/balance.service";
import { EsimService } from "../esim/esim.service";
import { MobileService } from "../mobile/mobile.service";
import { BillingService } from "../billing/billing.service";
import { Router } from "@angular/router";
import { PinPukModalComponent } from "../../components/modals/pinpuk-modal.component";
import { Auth } from "aws-amplify";
import { TransferDetailsModalComponent } from "../../components/modals/transfer-details-modal.component";
import { GlobalDataService } from "../../services/global-data.service";
import { detectEnv } from "src/app/services/Enviroment";
import { TranslateService } from "@ngx-translate/core";

/**
 * Componente principal del panel de usuario autenticado.
 * Gestiona información relacionada con el usuario, incluyendo:
 * - eSIMs
 * - contratos móviles
 * - facturas
 * - transferencias
 * - país y atributos de Cognito
 * También se conecta con múltiples servicios como Lambda, MobileService, EsimService, entre otros.
 */
@Component({
  selector: "app-home",
  templateUrl: "./dashboard.component.html",
  styleUrls: ['../esim/esim.component.scss'],
  styles: [
    `
      .loading-mask {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(
          0,
          0,
          0,
          0.5
        ); /* semi-transparent black background */
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999; /* Ensure it's on top of other content */
      }

      .inactive-warning-box {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        background-color: #FFF3E0;
        border-left: 4px solid #FF9800;
        border-radius: 8px;
        padding: 10px 14px;
        margin: 0 8px;
      }

      .inactive-warning-box .warning-icon {
        color: #FF9800;
        font-size: 20px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .inactive-warning-box .warning-content {
        flex: 1;
      }

      .inactive-warning-box .warning-text {
        margin: 0;
        color: #E65100;
        font-size: 13px;
        line-height: 1.4;
      }

      .inactive-warning-box .warning-link {
        color: #C50B0F;
        text-decoration: underline;
        font-weight: 500;
        cursor: pointer;
      }

      .inactive-warning-box .warning-link:hover {
        color: #A00A0C;
      }
    `,
  ],
})



export class HomeDashboardComponent implements OnDestroy {

  private _subscriptions = new Subscription();

  /** Muestra u oculta el spinner de carga global */
  showSpinner = false;

  /** Referencia global al objeto window */
  window = window;

  /** Controlador de modales */
  dialog = inject(MatDialog);

  /** Servicio para invocar funciones Lambda */
  lambda = inject(AWSLambda);

  /** Servicio de balance */
  private balanceSvc = inject(BalanceService);

  /** Entorno actual detectado ('dev', 'prod', etc.) */
  protected currentEnv = detectEnv();

  /** Usuario autenticado de Amplify */
  user!: AmplifyUser;

  /** Atributos de usuario desde Cognito */
  attributes: CognitoUserAttribute[] = [];

  /** Código de país del usuario (custom:country) */
  userCountry: string = '';

  /** Lista de eSIMs asociadas al usuario */
  userEsims: any[] = [];

  /** Contador de eSIMs inactivas (NOT_ACTIVE) */
  inactiveEsimsCount: number = 0;

  /** Primera eSIM seleccionada */
  esim: any;

  /** Contratos móviles del usuario */
  userContracts: any[] = [];

  /** Facturas del usuario */
  userBills: any[] = [];

  /** Contador de registros en las listas */
  filteredCounts = {
    userBills: 0,
    transactions: 0,
  };

  /** Transferencias del usuario */
  transactions: any[] = [];

  /** Permite transferencias gratuitas si aplica */
  allow_free_transfer: boolean = false;

  /** Balance actual del usuario */
  balance!: number;

  /** Moneda del usuario (por defecto EUR) */
  currency: string = `EUR`;

  /** Posición del botón (center o between) */
  btn_position: string = "center";

  /** Controla visibilidad del loader de eSIM */
  esimLoader: boolean = true;

  /** Spinners individuales para secciones móviles, facturas y transferencias */
  showSpinnerMobile: boolean = true;
  showSpinnerBills: boolean = true;
  showSpinnerTransfers: boolean = true;

  /** URLs utilizadas para enlaces externos */
  transfersUrl = "";
  creditsUrl = "";
  transfersAccountURL = `${inject(ConfigService).config.transfersAccountURL}?tracker=${inject(AuthService).getUser().id}`;
  mobileUrl = `${inject(ConfigService).config.mobileUrl}`;

  /** Listas de filtros para las listas */
  filterBillsFields: any[] = [];
  filterTransactionFields: any[] = [];

  /** Servicio de autenticación */
  authService = inject(AuthService);

  constructor(
    private esimService: EsimService,
    private mobileService: MobileService,
    private billingService: BillingService,
    private router: Router,
    private globalDataService: GlobalDataService,
    @Inject(TUI_IS_MOBILE) readonly isMobile: boolean,
    @Inject(TUI_COUNTRIES_MASKS)
    private readonly countriesMasks: Record<TuiCountryIsoCode, string>,
    private translate: TranslateService
  ) {
    this.transfersUrl = this.globalDataService.transfersUrlData;
    this.creditsUrl = this.globalDataService.creditsUrlData;
  }

  /**
   * Inicializa el componente cargando:
   * - usuario autenticado
   * - atributos de usuario
   * - país
   * - balance
   * - eSIMs
   * - contratos móviles
   * - facturas
   * - transferencias
   */
  async ngOnInit() {
    try {
      const allowedEnvStr = localStorage.getItem('allowedEnvs') || '';
      const allowedEnvs = allowedEnvStr.split(',').map(e => e.trim().toLowerCase());

      const env = this.currentEnv; // 'dev' | 'prod'

      if (!allowedEnvs.includes(env)) {
        await Auth.signOut();
        throw new Error(`No tienes permiso para entrar a este entorno ${env.toUpperCase()}`);
      }

      // comprueba si se quiso comprar alguna esim antes del registro para llevar de nuevo al usuario a la vista de detalles de la que seleccionó
      const onSignupEsimData = sessionStorage.getItem('onsignup_esim_data');
      if (onSignupEsimData) {
        this.router.navigate(['/esim-details'], { state: { data: JSON.parse(onSignupEsimData) } });
        return;
      }

      // if (this.currentEnv === 'dev') {
      //   console.log('Ambiente: Desarrollo');
      // } else if (this.currentEnv === 'prod') {
      //   console.log('Ambiente: Producción');
      // } else {
      //   console.warn('Ambiente no reconocido');
      // }
      await this.authService.refresh();
      this.user = await Auth.currentAuthenticatedUser();
      this.attributes = await Auth.userAttributes(this.user);

      this.userCountry = this.attributes.find(attribute => attribute.Name === 'custom:country')?.Value ?? ''

      const cachedBalance = this.balanceSvc.getCachedBalance();
      if (cachedBalance !== null) {
        this.balance = cachedBalance;
      } else {
        this._subscriptions.add(
          this.balanceSvc.balance$.pipe(filter(b => b !== null), take(1))
            .subscribe(b => { this.balance = b!; })
        );
      }

      this.getUserEsimList();
      this.esim = this.userEsims[0];

      // this.getUserMobileContracts(); // OPERADORA MÓVIL DESACTIVADA
      this.getUserBills();

      this.showSpinnerTransfers = true;
      this.transactions = await this.getTransactions();
      //console.log(this.transactions)

      this.showSpinnerTransfers = false;

    } catch (error) {
      this.authService.logout();
    }
  }

  /**
  * Abre un modal con los detalles de una transferencia.
  * @param transaction Objeto con datos de la transacción
  */
  viewTransferDetails(transaction: any) {
    //console.log(transaction)
    this.dialog.open(TransferDetailsModalComponent, {
      minWidth: this.isMobile ? '95%' : '30%',
      panelClass: 'zendy-modal',
      data: transaction
    });
  }

  /**
   * Retorna el nombre completo de un usuario desde un arreglo de campos.
   */
  getFullName(fields: { name: string, value: string }[]): string {
    const firstName = fields.find(f => f.name === 'firstName')?.value || '';
    const lastName = fields.find(f => f.name === 'lastName')?.value || '';
    return `${firstName} ${lastName}`;
  }

  /**
   * Obtiene la lista de transacciones desde Lambda.
   */
  async getTransactions(): Promise<[]> {
    try {
      // Transactions IDs: 1988, 1990, 1991, 1993
      const response = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
        queryStringParameters: {
          method: 'transactions',
          //id: id,
          sub: this.attributes.find(attribute => attribute.Name === 'sub')?.Value ?? ''
        }
      });
      const this_month_transaction = thisMonthTransaction(response.transactions)
      this.globalDataService.allowFreeTransfer = this.globalDataService.allowFreeTransfer && !this_month_transaction;
      //console.log('Allow free: ' + this.globalDataService.allowFreeTransfer)

      const sortedArray = response.transactions.sort((a:any, b:any) => {
        return new Date(b.transaction.created).getTime() - new Date(a.transaction.created).getTime();
      });

      const uniqueCountries = Array.from(
        new Set(sortedArray.map((item: { country: any; }) => item.country))
      ).map(country => ({
        key: country,
        value: country
      })
      );

      this.filterTransactionFields = [
        {
          key: 'fullName',
          type: 'text',
          label: this.translate.instant('Nombre'),
          searchKeys: ['beneficiary_fields.firstName', 'beneficiary_fields.lastName']
        },
        {
          key: 'country',
          type: 'select',
          label: this.translate.instant('TRANSFERS.BENEFICIARY_COUNTRY'),
          options: uniqueCountries
        },
        {
          key: 'transaction.created',
          type: 'date-range',
          label: this.translate.instant('COMMON.BILLDATE')
        },
        {
          key: 'transaction.payment_amount',
          type: 'range-slider',
          label: this.translate.instant('TRANSFERS.TOTAL_PAID'),
          min: 0,
          max:5000
        }
      ];

      return sortedArray;
    } catch (error) {
      console.error('Error calling Lambda function:', error);
      return []; // Tratar el error como que el usuario no existe
    }
  }

  /**
   * Obtiene los contratos móviles del usuario.
   */
  async getUserMobileContracts(): Promise<void> {
    this.userContracts = await this.mobileService.getContracts();
    this.showSpinnerMobile = false;
  }

  /**
  * Obtiene las facturas asociadas al usuario.
  * @param type Tipo de factura
  * @param contract Contrato específico
  * @param from Fecha desde
  * @param to Fecha hasta
  */
  async getUserBills(type: string = "", contract: string = "", from: string = "", to: string = ""): Promise<void> {
    // let ispinvoices = await this.mobileService.getBills(contract, from, to); // OPERADORA MÓVIL DESACTIVADA
    let holdedinvoices = await this.billingService.getBills('invoice', from, to)
    let holdedreceipts = await this.billingService.getBills('salesreceipt', from, to)

    this.userBills = [...holdedinvoices, ...holdedreceipts]
    this.userBills.sort((a, b) => b.fecha - a.fecha);

    this.filterBillsFields = [
      {
        key: 'type',
        type: 'select',
        label: this.translate.instant('ESIM.TYPE'),
        options: [
          { key: -1, value: this.translate.instant('COMMON.ALL') },
          { key: 'MOBILE', value: this.translate.instant('COMPONENTS.HEADER.MOBILE_OPERATOR') },
          { key: 'ESIM', value: this.translate.instant('COMPONENTS.TABS.TAB5') },
          { key: 'CALLS', value: this.translate.instant('COMPONENTS.HEADER.CALLS') }
        ]
      },
      {
        key: 'situacion',
        type: 'select',
        label: this.translate.instant('COMMON.STATUS'),
        options: [
          { key: -1, value: this.translate.instant('COMMON.ALL') },
          { key: 0, value: this.translate.instant('BILLS.PENDING') },
          { key: 1, value: this.translate.instant('BILLS.PAID') }
        ]
      },
      {
        key: 'fecha',
        type: 'date-range',
        label: this.translate.instant('COMMON.BILLDATE')
      }
    ];

    this.showSpinnerBills = false;
  }

  /**
  * Descarga una factura como PDF.
  * @param type Tipo de factura
  * @param id Identificador del documento
  * @param name Nombre del archivo
  */
  async downloadBill(type: string, id: string, name: string) {
    this.showSpinner = true;
    let data, source;
    if (type == "MOBILE") {
      data = await this.mobileService.getBill(id);
    } else {
      data = await this.billingService.getBill(type, id);
    }
      source = 'data:application/octet-stream;base64,' + data.data;
      const link = document.createElement("a");
      link.href = source;
      link.download = `Zendy-${name}.pdf`;
      link.click();

    this.showSpinner = false;

  }

  /**
   * Abre un modal con los datos de PIN/PUK del contrato.
   */
  async show_pin_puk(contract: any) {
    console.log(contract)
    /*let pinpuk = await this.mobileService.getPinPuk(iccid);
    console.log(pinpuk)
    */

    this.dialog.open(PinPukModalComponent, {
      minWidth: this.isMobile ? '95%' : '30%',
      panelClass: 'zendy-modal',
      data: contract
    });
  }

  /**
   * Calcula el porcentaje usado respecto al total.
   */
  calculatePercentage(total: number | undefined, used: number | undefined): number {
    if (!total || !used) {
      return 0;
    }
    return (used / total) * 100;
  }

  /**
   * Abre el detalle de una eSIM del usuario.
   * @param esimData Datos de la eSIM del usuario
   */
  openEsimDetailsDialog(esimData: any): void {
    console.log('>> User eSIM details from dashboard', esimData);

    // Store in sessionStorage as fallback
    sessionStorage.setItem('userEsimDetails', JSON.stringify(esimData));

    // Navigate to user eSIM details page (not purchase page)
    this.router.navigate(['/user-esim-details', esimData.details.iccid], {
      state: { esimData: esimData },
      queryParams: {
        view: 'dashboard'  // Track that user came from dashboard
      }
    });
  }

  /**
  * Obtiene la lista de eSIMs asociadas al usuario.
  * Usa la caché de GlobalDataService si está disponible; si hay carga en background,
  * espera a que finalice; si no hay caché ni carga activa, hace fetch directo.
  */
  async getUserEsimList(): Promise<void> {
    const cached = this.globalDataService.getUserEsims();
    if (cached) {
      this._applyEsimData(cached);
      return;
    }

    if (this.globalDataService.isUserEsimsLoading()) {
      this._subscriptions.add(
        this.globalDataService.userEsimsLoading$.pipe(
          filter(loading => !loading),
          take(1)
        ).subscribe(() => {
          this._applyEsimData(this.globalDataService.getUserEsims() ?? []);
        })
      );
      return;
    }

    // Sin caché y sin carga en background → fetch directo
    const allEsims = await this.esimService.getEsimList();
    this._applyEsimData(allEsims ?? []);
  }

  private _applyEsimData(allEsims: any[]): void {
    this.userEsims = allEsims.filter((esim: any) => esim.esim_status === 'ACTIVE');
    this.inactiveEsimsCount = allEsims.filter((esim: any) => esim.esim_status === 'NOT_ACTIVE').length;
    this.globalDataService.userEsimsCount = allEsims.length;
    this.esimLoader = false;
    this.btn_position = this.userEsims.length > 0 ? "between" : "center";
  }

  goToEsimSection(): void {
    if (this.globalDataService.userEsimsCount > 0) {
      this.router.navigate(['/my-esims']);
    } else {
      this.router.navigate(['/esim']);
    }
  }

  /**
   * Calcula el balance actual del usuario.
   */
  async calculateBalance(): Promise<number> {
    return await this.balanceSvc.calculateBalance();
  }

  /**
   * Abre un modal con la lista de países disponibles para tarifas.
   */
  async openCountriesModal() {
    this.showSpinner = true;
    try {
      const res = await this.lambda.getLambdaFunction('lambda', '/get/contries', {
        queryStringParameters: {
          method: 'allowedToCall'
        },
      });
      if (res.ok) {
        const isoList: string[] = res.countries || [];

        // Filtra los países válidos
        const filteredCountries: TuiCountryIsoCode[] = isoList
          .map(code => code.toUpperCase())  // Convertimos a mayúsculas
          .filter(code => Object.values(TuiCountryIsoCode).includes(code as TuiCountryIsoCode))  // Filtramos los códigos válidos
          .map(code => code as TuiCountryIsoCode);  // Aseguramos que el tipo es TuiCountryIsoCode

        // Abre el modal con los países ya cargados
        this.dialog
          .open(CountriesModalComponent, {
            width: this.isMobile ? '85%' : '30%',
            panelClass: 'zendy-modal',
            data: filteredCountries,  // Pasamos los países filtrados al modal
          })
          .afterClosed()
          .subscribe(async (countryCode: TuiCountryIsoCode) => {
            const rates: Rate = await this.getPricing(countryCode);
            if (!rates) return;
            this.openRatesModal(countryCode, rates);
          });
      } else {
        console.error('Error en la respuesta:', res.mensaje);
      }

    } catch (error) {
      console.error('Error al obtener países:', error);
    }
    this.showSpinner = false;
  }

  /**
   * Abre un modal con las tarifas de un país.
   * @param country Código ISO del país
   * @param rates Tarifas a mostrar
   */
  openRatesModal(country: TuiCountryIsoCode, rates: Rate) {
    this.showSpinner = true;
    const data: RatesModal = {
      country,
      pricing: rates.pricing,
      prefix: rates.to,
    };
    this.dialog.open(RatesModalComponent, {
      minWidth: this.isMobile ? '95%' : '30%',
      panelClass: 'zendy-modal',
      data,
    });
    this.showSpinner = false;
  }

  /**
   * Obtiene las tarifas para llamadas internacionales.
   * @param to Código ISO del país destino
   */
  async getPricing(to: TuiCountryIsoCode) {
    if (this.countriesMasks[to]) {
      const countryCode = this.countriesMasks[to].replace(/[()\s#-]/g, '');

      const isoCode = getCountryInfo(countryCode, to, "ISO-3");

      return await this.lambda.getLambdaFunction('lambda', '/pricing/calls', {
        queryStringParameters: {
          from: '+34',
          to: countryCode,
          via: 'signalwire',
          filter: isoCode // Regexpress - aqui deberia ir el ISO CODE o el nombre del pais en ingles.
        },
      });
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  goToTransfers() {
    this.router.navigate(['/transfers']);
  }

  //////////////////////////////////////////////////////////
  //////                                              //////
  ////// INICIO DE MÉTODOS NECESARIOS PARA LAS LISTAS //////
  //////                                              //////
  //////////////////////////////////////////////////////////
  /**
   * Método para invocar desde componente padre al hijo para la acción sobre click del elemento
   * @param item registro de la lista
   */
  onItemClick(item: any) {
    console.log('Clicked item:', item);
  }
  /**
   * Método para invocar desde componente padre al hijo para el cambio en los filtros
   * @param filters valor de los filstros cambiados
   */
  onFiltersChanged(filters: any) {
    console.log('Filters updated:', filters);
  }
  /**
   * Método para invocar desde componente padre al hijo para la paginación
   * @param page número de página a mostrar
   */
  onPageChanged(page: number) {
    console.log('Page changed to:', page);
  }
  /**
   * Método para invocar desde componente padre al hijo para la edición del registro
   * Opcional. Depende de las acciones que se quieran realizar sobre el registro
   * @param item registro de la lista
   */
  editItemList(item: any) {
    console.log('Editando', item);
    // tu lógica aquí
  }
  /**
   * Método para invocar desde componente padre al hijo para el borrado del registro
   * Opcional. Depende de las acciones que se quieran realizar sobre el registro
   * @param item registro de la lista
   */
  deleteItemList(item: any) {
    console.log('Eliminando', item);
    // tu lógica aquí
  }
  ///////////////////////////////////////////////////////
  //////                                           //////
  ////// FIN DE MÉTODOS NECESARIOS PARA LAS LISTAS //////
  //////                                           //////
  ///////////////////////////////////////////////////////

}

/**
 * Verifica si existe alguna transacción en el mes actual.
 * @param transactions Lista de transacciones
 * @returns true si existe una transacción este mes
 */
function thisMonthTransaction(transactions: any[]): boolean {
  const now = new Date();
  //now.setMonth(now.getMonth()+1) // Simular otro mes
  return transactions.some(t => {
    const created = new Date(t.transaction.created);
    return created.getMonth() === now.getMonth() &&
      created.getFullYear() === now.getFullYear();
  });
}
